/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Toast, GoTop } from '@components/atoms'

import '@mdi/font/css/materialdesignicons.css'
import { GlobalComponentsContextProvider } from '@contexts'
import { Logger } from '@utils'
import {
	useAppDispatch,
	configActions,
	useAppSelector,
	ConfigStateType,
	CartType,
} from '@redux/index'
import config from '@configs'
import { isMobile } from 'react-device-detect'
import { SeasonProductType } from '@schemas'

interface PropsType {
	children: React.ReactNode
}

export const AppWrapper: React.FC<PropsType> = props => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const storeSystemName: string = useAppSelector(state => state.config.systemName)
	const toast: ConfigStateType['toast'] = useAppSelector(state => state.config.toast)
	const seasonProductList: SeasonProductType[] = useAppSelector(
		state => state.shop.productList,
	)
	const currentPath: string = useAppSelector(state => state.route.currentPath)
	// 서버가 판정한 현재 판매 시즌. 예전에는 .env 의 REACT_APP_SEASON_ID 였다.
	const currentSeasonId: string = useAppSelector(state => state.shop.seasonId)

	// toast state
	const [toastMessage, setToastMessage] = React.useState<string>('')
	const [showToast, setShowToast] = React.useState<boolean>(false)
	const [localCartList, setLocalCartlist] = React.useState<CartType[]>([])
	const [showGoTop, setShowGoTop] = React.useState(false)

	React.useEffect(() => {
		Logger.env()

		window.addEventListener('scroll', handleScroll)
		return () => {
			window.removeEventListener('scroll', handleScroll)
		}
	}, [])

	const handleScroll = () => {
		if (window.scrollY > 300) {
			setShowGoTop(true)
		} else {
			setShowGoTop(false)
		}
	}

	const scrollToTop = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}

	// currentSeasonId 는 shop 응답이 온 뒤에 채워진다. 그전에는 비교 기준이 없으므로
	// 검사를 건너뛰고, 값이 들어온 시점에 다시 판단한다.
	React.useEffect(() => {
		getLocalCartList()
	}, [currentPath, currentSeasonId])

	React.useEffect(() => {
		reduxDispatch(configActions.IS_MOBILE(isMobile))
	}, [isMobile])

	React.useEffect(() => {
		const systemEnv = config.get('system')
		const systemName: string = systemEnv.name.replaceAll(/-/g, ' ')
		const systemSubtitle: string = systemEnv.subtitle
		const title = systemName

		document.title = title

		if (storeSystemName !== systemName) {
			reduxDispatch(configActions.SYSTEM_NAME(systemName))
		}

		if (systemSubtitle !== '') {
			reduxDispatch(configActions.SYSTEM_SUBTITLE(systemSubtitle))
		}
	}, [storeSystemName])

	React.useEffect(() => {
		if (toast.isShow) {
			setToastMessage(toast.message)
			setShowToast(true)
			reduxDispatch(configActions.TOAST({ isShow: false, message: '' }))
		}
	}, [toast])

	React.useEffect(() => {
		if (localCartList.length > 0) {
			localCartList.map(anItem => {
				const idx = seasonProductList.findIndex(anItem2 => anItem2.id === anItem.id)

				if (idx > -1) {
					const isValidSeasonUsd = seasonProductList[idx].seasonUsd === anItem.seasonUsd

					if (!isValidSeasonUsd) {
						localStorage.removeItem('cart')
						window.dispatchEvent(new Event('storage'))
					}
				}
			})
		}
	}, [localCartList, seasonProductList])

	const getLocalCartList = () => {
		if (
			!currentPath.startsWith('/main') &&
			!currentPath.startsWith('/auth') &&
			!currentPath.startsWith('/admin')
		) {
			const localeCartSrt = localStorage.getItem('cart')
			const res: CartType[] =
				localeCartSrt === null || localeCartSrt === '' ? [] : JSON.parse(localeCartSrt)

			if (res.length > 0) {
				// 시즌이 바뀌면 지난 시즌 가격의 장바구니를 버린다.
				// seasonId 가 아직 비어 있으면(응답 전) 판단을 미룬다.
				if (currentSeasonId !== '' && res[0].seasonId !== currentSeasonId) {
					localStorage.removeItem('cart')
					window.dispatchEvent(new Event('storage'))
				}

				setLocalCartlist(res)
			}
		}
	}

	const onShowToast = (message: string) => {
		setToastMessage(message)
		setShowToast(true)
	}

	return (
		<GlobalComponentsContextProvider onShowToast={onShowToast}>
			<View css={style}>
				{props.children}

				<GoTop show={showGoTop} onClick={scrollToTop} />
				<Toast show={showToast} title={toastMessage} onToggleToast={setShowToast} />
			</View>
		</GlobalComponentsContextProvider>
	)
}

const style = css({})
